import { useState, useLayoutEffect, useEffect, useRef } from "react";
import { CSSTransition } from "react-transition-group";
import { useTranslation } from "react-i18next";
import { H3, SubTitle3 } from "@components/new/Typo";
import {
  Button,
  ButtonGroup,
  ButtonSize,
  ButtonType,
} from "@components/new/Button";
import ButtonWithTimer from "@components/new/ButtonWithTimer/ButtonWithTimer";
import {
  IconButton,
  IconButtonSize,
  IconButtonType,
} from "@components/new/IconButton";
import { ModalSize } from "./types";
import { Portal } from "@components/Portal";
import XIcon from "@assets/icons/outline/x.svg?react";
import styles from "./Modal.module.scss";
import cn from "classnames";

interface FooterButtonProps {
  title: string;
  icon?: string | React.FunctionComponent<React.SVGAttributes<SVGElement>>;
  loading?: boolean;
  disabled?: boolean;
  type?: ButtonType;
  onClick?: () => void;
  timeout?: number;
  startTimerFromOutside?: boolean;
  isWide?: boolean;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  size: ModalSize;
  noScroll?: boolean;
  confirmCancel?: boolean;
  confirmCancelDescription?: string;
  footerButtons?: FooterButtonProps[];
  headerContent?: React.ReactNode;
  headerTitleContent?: React.ReactNode;
  inModal?: boolean;
  className?: string;
}

export const ModalHeader = ({
  title,
  titleContent,
  handleCancel,
  children,
}: {
  title?: React.ReactNode;
  titleContent?: React.ReactNode;
  handleCancel: () => void;
  children?: React.ReactNode;
}) => (
  <div className={styles.header}>
    <div className={styles.headerTitle}>
      <H3 ellipsis lines={3} weight="medium">
        {title}
      </H3>
      {titleContent}
      <div className={styles.xIcon}>
        <IconButton
          icon={XIcon}
          onClick={handleCancel}
          size={IconButtonSize.L}
          type={IconButtonType.ICON}
        />
      </div>
    </div>
    {children ? <div className={styles.headerContent}>{children}</div> : null}
  </div>
);

export const ModalHeaderConfirm = ({
  onCancel,
  onClose,
  size,
  confirmCancelDescription,
}: {
  onCancel: () => void;
  onClose: () => void;
  size: ModalSize;
  confirmCancelDescription?: string;
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn(styles.header, styles.headerConfirm)}>
      <div className={styles.headerTitle}>
        <H3>{t("modal.confirmClose.title")}</H3>
        <div className={styles.xIcon}>
          <IconButton
            icon={XIcon}
            onClick={onClose}
            size={IconButtonSize.L}
            type={IconButtonType.ICON}
          />
        </div>
      </div>
      <SubTitle3 color="grey-800">
        {confirmCancelDescription || t("modal.confirmClose.description")}
      </SubTitle3>
      <ButtonGroup align={size === ModalSize.L ? "start" : "stretch"}>
        <Button
          size={ButtonSize.L}
          type={ButtonType.ATTENTION}
          title={t("modal.confirmClose.closeBtn")}
          onClick={onClose}
        />
        <Button
          size={ButtonSize.L}
          type={ButtonType.OUTLINE}
          title={t("modal.confirmClose.cancelBtn")}
          onClick={onCancel}
        />
      </ButtonGroup>
    </div>
  );
};

export const ModalFooter = ({
  children,
  size,
}: {
  children: React.ReactNode;
  size: ModalSize;
}) => (
  <div className={styles.footer}>
    <ButtonGroup align={size === ModalSize.L ? "end" : "stretch"}>
      {children}
    </ButtonGroup>
  </div>
);

export const Modal = (props: ModalProps) => {
  const nodeRef = useRef(null);
  const overlayRef = useRef(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScroll] = useState(false);
  const [isCloseConfirmationVisible, setCloseConfirmationVisible] =
    useState(false);

  const handleCancel = () => {
    if (props.confirmCancel) {
      setCloseConfirmationVisible(true);
    } else {
      props.onClose();
    }
  };

  useLayoutEffect(() => {
    if (props.isOpen) {
      document.getElementById("root")?.classList.add("blurred");
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.getElementById("root")?.classList.remove("blurred");
      document.body.style.overflow = "auto";
    };
  }, [props.isOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      setHasScroll(
        scrollRef.current.clientHeight < scrollRef.current.scrollHeight,
      );
    }
  }, [scrollRef.current]);

  return (
    <Portal>
      <CSSTransition
        in={props.isOpen}
        timeout={300}
        classNames={{
          enter: styles.modalEnter,
          enterActive: styles.modalEnterActive,
          exit: styles.modalExit,
          exitActive: styles.modalExitActive,
        }}
        mountOnEnter
        unmountOnExit
        nodeRef={nodeRef}
      >
        <div
          className={cn(styles.wrapper, props.inModal && styles.inner)}
          onClick={(e) => {
            e.stopPropagation();
            if (e.target !== overlayRef.current) return;
            handleCancel();
          }}
          ref={overlayRef}
        >
          <div
            className={cn(
              "modal",
              styles.modal,
              styles[`size-${props.size}`],
              props.className,
              props.noScroll && styles.noScroll,
            )}
            ref={nodeRef}
          >
            {props.size === ModalSize.S ? null : (
              <>
                <ModalHeader
                  title={props.title}
                  handleCancel={handleCancel}
                  titleContent={props.headerTitleContent}
                >
                  {props.headerContent}
                </ModalHeader>
                {isCloseConfirmationVisible ? (
                  <ModalHeaderConfirm
                    size={props.size}
                    onClose={props.onClose}
                    onCancel={() => setCloseConfirmationVisible(false)}
                    confirmCancelDescription={props.confirmCancelDescription}
                  />
                ) : null}
              </>
            )}
            <div className={styles.content}>
              <div
                ref={scrollRef}
                className={cn(styles.contentInner, hasScroll && styles.scroll)}
              >
                {props.children}
              </div>
            </div>
            {props.footerButtons?.length ? (
              <ModalFooter size={props.size}>
                {(props.footerButtons ?? []).map((item, idx) =>
                  item.timeout ? (
                    <ButtonWithTimer
                      key={idx}
                      onClick={item.onClick ?? (() => {})}
                      pending={item.loading}
                      type={
                        item.type ??
                        (idx > 0 ? ButtonType.PRIMARY : ButtonType.SECONDARY)
                      }
                      title={item.title}
                      timeout={item.timeout}
                      startTimerFromOutside={item.startTimerFromOutside}
                      // disabled={item.disabled}
                    />
                  ) : (
                    <Button
                      key={idx}
                      size={ButtonSize.L}
                      type={
                        item.type ??
                        (idx > 0 ? ButtonType.PRIMARY : ButtonType.SECONDARY)
                      }
                      title={item.title}
                      icon={item.icon}
                      onClick={item.onClick ?? handleCancel}
                      loading={item.loading}
                      disabled={item.disabled}
                      isWide={item.isWide}
                    />
                  ),
                )}
              </ModalFooter>
            ) : null}
          </div>
        </div>
      </CSSTransition>
    </Portal>
  );
};
